
import { supabase } from '@/integrations/supabase/client';
import { ContactInquiry, ContactFormData, InquiryStatus } from '@/lib/types/contact-types';

/**
 * Submit a new contact inquiry
 */
export async function submitContactInquiry(formData: ContactFormData): Promise<{ error?: Error }> {
  try {
    // Use type assertion to bypass TypeScript's type checking for the table name
    const { error } = await supabase
      .from('contact_inquiries' as any)
      .insert([{
        ...formData,
        status: 'open',
      }]);
    
    if (error) throw error;
    return {};
  } catch (error) {
    console.error('Error submitting contact inquiry:', error);
    return { error: error as Error };
  }
}

/**
 * Fetch contact inquiries with pagination and optional status filter
 */
export async function getContactInquiries(
  page: number = 1, 
  pageSize: number = 10, 
  statusFilter: InquiryStatus | 'all' = 'all'
): Promise<{ data: ContactInquiry[], count: number | null, error?: Error }> {
  try {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize - 1;
    
    // Build query with type assertion
    let query = supabase
      .from('contact_inquiries' as any)
      .select('*', { count: 'exact' });
    
    // Apply status filter if not 'all'
    if (statusFilter !== 'all') {
      query = query.eq('status', statusFilter);
    }
    
    // Add pagination and order
    const { data, error, count } = await query
      .order('created_at', { ascending: false })
      .range(startIndex, endIndex);
    
    if (error) throw error;
    
    return { 
      data: data as unknown as ContactInquiry[], 
      count, 
      error: undefined 
    };
  } catch (error) {
    console.error('Error fetching contact inquiries:', error);
    return { data: [], count: null, error: error as Error };
  }
}

/**
 * Update the status of a contact inquiry
 */
export async function updateInquiryStatus(
  id: string, 
  status: InquiryStatus
): Promise<{ data: ContactInquiry | null, error?: Error }> {
  try {
    // Use type assertion to bypass TypeScript's type checking
    const { data, error } = await supabase
      .from('contact_inquiries' as any)
      .update({ 
        status,
        ...(status === 'resolved' ? { resolved_at: new Date().toISOString() } : {}),
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select('*')
      .single();
    
    if (error) throw error;
    
    return { data: data as unknown as ContactInquiry };
  } catch (error) {
    console.error('Error updating inquiry status:', error);
    return { data: null, error: error as Error };
  }
}
